<script setup>
import moment from 'moment'
import { Check, X } from 'lucide-vue-next'
import ChatAvatar from '@/pages/Project/Chat/ChatAvatar.vue'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

defineProps({
  requests: { type: Array, default: () => [] },
  size: { type: String, default: 'sm' }, // 'xs' | 'sm'
  manageable: { type: Boolean, default: false },
})
const emit = defineEmits(['approve', 'decline'])
</script>

<template>
  <Popover v-if="requests.length">
    <PopoverTrigger as-child>
      <button
        type="button"
        class="flex shrink-0 items-center whitespace-nowrap rounded-full py-0.5 pl-0.5 pr-2 transition-colors hover:bg-muted"
        title="People waiting for approval"
        data-testid="join-requests"
      >
        <div class="flex items-center">
          <ChatAvatar
            v-for="(member, i) in requests.slice(0, 3)"
            :key="member.member_id"
            :author="member"
            :size="size"
            class="ring-2 ring-background"
            :class="i > 0 ? (size === 'xs' ? '-ml-1.5' : '-ml-2') : ''"
          />
        </div>
        <span class="ml-1.5 font-medium text-amber-700 dark:text-amber-300" :class="size === 'xs' ? 'text-xs' : 'text-sm'">
          {{ requests.length }} {{ requests.length === 1 ? 'request' : 'requests' }}
        </span>
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" class="w-96 p-2">
      <p class="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Join requests</p>
      <ul class="max-h-80 divide-y overflow-y-auto">
        <li v-for="member in requests" :key="member.member_id" class="flex items-start gap-3 px-2 py-2.5" data-testid="join-request">
          <div class="size-12 shrink-0">
            <ChatAvatar :author="member" size="full" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium leading-5">{{ member.display_name }}</p>
            <p v-if="member.email" class="break-all text-sm text-muted-foreground">{{ member.email }}</p>
            <p class="text-xs text-muted-foreground">Asked {{ moment(member.requested_at).fromNow() }}</p>
            <div v-if="manageable" class="mt-1.5 flex gap-1.5">
              <Button type="button" size="sm" class="h-7 px-2 text-xs" data-testid="approve-request" @click="emit('approve', member)">
                <Check class="mr-1 size-3.5" />
                Approve
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                class="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                data-testid="decline-request"
                @click="emit('decline', member)"
              >
                <X class="mr-1 size-3.5" />
                Decline
              </Button>
            </div>
          </div>
        </li>
      </ul>
    </PopoverContent>
  </Popover>
</template>
